"use client"

import { t } from "@/languages/i18n"
/**
 * StatusCell — bordered pill badge (Strapi-style) for list table status columns.
 *
 * Uses the admin OKLch status tokens (--admin-accent / warning / info
 * / danger / muted-fg) as both border and text color, with a soft tint
 * background.
 */

import type { ReactNode } from "react"

export type StatusTone = "success" | "warning" | "info" | "danger" | "muted"

type PresetKey =
  | "published"
  | "active"
  | "draft"
  | "inactive"
  | "pending"
  | "scheduled"
  | "rejected"
  | "archived"

const PRESETS: Record<PresetKey, { tone: StatusTone; label: string }> = {
  published: { tone: "success", label: t("Đã xuất bản") },
  active: { tone: "success", label: t("Hoạt động") },
  draft: { tone: "muted", label: t("Nháp") },
  inactive: { tone: "muted", label: t("Không hoạt động") },
  pending: { tone: "warning", label: t("Chờ duyệt") },
  scheduled: { tone: "info", label: t("Đã lên lịch") },
  rejected: { tone: "danger", label: t("Bị từ chối") },
  archived: { tone: "muted", label: t("Lưu trữ") },
}

const TONE_COLOR: Record<StatusTone, string> = {
  success: "var(--admin-accent)",
  warning: "var(--admin-warning)",
  info: "var(--admin-info)",
  danger: "var(--admin-danger)",
  muted: "var(--admin-muted-fg)",
}

interface StatusCellProps {
  /**
   * Either a preset key ("published" / "draft" / ...) or a custom
   * { tone, label } pair for non-standard statuses.
   */
  status: PresetKey | { tone: StatusTone; label: ReactNode }
}

export function StatusCell({ status }: StatusCellProps) {
  const resolved =
    typeof status === "string"
      ? PRESETS[status] ?? { tone: "muted" as StatusTone, label: status }
      : status

  const color = TONE_COLOR[resolved.tone]

  return (
    <span
      className="inline-flex items-center"
      style={{
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.4,
        color,
        border: `1px solid ${color}`,
        background: `color-mix(in oklch, ${color} 12%, transparent)`,
        borderRadius: 6,
        padding: "2px 10px",
      }}
    >
      {resolved.label}
    </span>
  )
}

export default StatusCell
